import { z } from "zod"
import type { BoundingBox } from "@/components/pdf/pdf-document-viewer"

const MAX_SOURCE_PAYLOAD_LENGTH = 4096

const fraction = z.number().min(0).max(1)

const sourcePayloadSchema = z.object({
  version: z.literal(1),
  evidence: z.array(z.string().trim().min(1).max(500)).max(10),
  boxes: z
    .array(
      z.object({
        page: z.number().int().positive(),
        left: fraction,
        top: fraction,
        width: fraction,
        height: fraction,
      }),
    )
    .max(30)
    .default([]),
})

export type DecodedSource = { evidence: string[]; boxes: BoundingBox[] }

const NO_SOURCE: DecodedSource = { evidence: [], boxes: [] }

export const sourceSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().catch(undefined),
  source: z.string().max(MAX_SOURCE_PAYLOAD_LENGTH).optional().catch(undefined),
})

export function decodeSource(source: string | undefined): DecodedSource {
  if (!source) return NO_SOURCE

  try {
    const normalized = source.replace(/-/g, "+").replace(/_/g, "/")
    const padding = "=".repeat((4 - (normalized.length % 4)) % 4)
    const parsed: unknown = JSON.parse(atob(`${normalized}${padding}`))
    const result = sourcePayloadSchema.safeParse(parsed)
    return result.success ? result.data : NO_SOURCE
  } catch {
    return NO_SOURCE
  }
}

export function decodeSourceEvidence(source: string | undefined): string[] {
  return decodeSource(source).evidence
}

export function encodeSource({ evidence, boxes }: DecodedSource): string {
  const payload = JSON.stringify({
    version: 1,
    evidence,
    ...(boxes.length > 0 ? { boxes } : {}),
  })
  const bytes = new TextEncoder().encode(payload)
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("")
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}
